<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue'
import plural from 'plural-ru'
import AudioPlayerV2 from '../../AudioPlayerV2.vue'
import { httpPost } from '@/lib/http'
import { AudioActivityTracker } from '@/helpers/trackers'
import type { Student2Context } from '@/types/student2'

function getRepeatCount(studentId: number) {
  const storageKey = `studentNightRepeatCount${studentId}`
  const stored = Number(localStorage[storageKey])
  return isNaN(stored) ? 1 : stored
}

const props = defineProps<{ context: Student2Context; canSubcharge: boolean }>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'add-stars', value: number): void
  (e: 'update:discharge', value: number): void
  (e: 'update:subcharge', value: number): void
  (e: 'add-subcharge', value: number): void
}>()

const isOpen = ref(false)
const materialId = ref<number | null>(null)
const repeatCount = ref(getRepeatCount(props.context.student.id))
const starCount = ref(props.context.smartbook?.awarded_night_stars_count ?? 0)
const dim = ref(false)
const closing = ref(false)
const oneStarInfo = ref(false)

const audio = ref<{ $el: HTMLElement } | null>(null)
const star = useTemplateRef<HTMLElement>('star')

let tracker: AudioActivityTracker | null = null
let subchargeTracker: AudioActivityTracker | null = null
let closeInterval: ReturnType<typeof setTimeout> | null = null

const formatPlural = (value = 0, one: string, two: string, five: string) => {
  return `${value} ${plural(value, one, two, five)}`
}

watch(repeatCount, val => {
  const studentId = props.context.student.id
  localStorage[`studentNightRepeatCount${studentId}`] = val.toString()
})

const material = computed(() => {
  if (!materialId.value) return null

  const m = props.context.audio_lessons.find(item => item.id === materialId.value)
  if (!m) return null

  return { name: m.name, id: m.id, rus: m }
})

const awardCount = computed(() => (props.context.course_part.codename === 'icanread' ? 5 : 1))
const awardCountWithNoun = computed(() => formatPlural(awardCount.value, 'звезда', 'звезды', 'звёзд'))

const close = () => {
  tracker?.onAudioPaused()
  tracker = null
  subchargeTracker?.onAudioPaused()
  subchargeTracker = null

  isOpen.value = false
  materialId.value = null
  repeatCount.value = getRepeatCount(props.context.student.id)
  starCount.value = props.context.smartbook?.awarded_night_stars_count ?? 0
  dim.value = false
  closing.value = false
  emit('close')

  if (closeInterval) clearTimeout(closeInterval)
}

const toggleStar = () => {
  if (!star.value) return console.warn('Tried to toggle a star, but they are not rendered')

  const starContainer = star.value
  const activeStar = starContainer.querySelector<HTMLElement>(':not([hidden])')
  const inactiveStar = starContainer.querySelector<HTMLElement>('[hidden]')
  if (!activeStar || !inactiveStar) return

  const confetti = document.createElement('div')
  confetti.className = 'confetti'
  starContainer.appendChild(confetti)

  const animatePromise = (element: HTMLElement, keyframes: Keyframe[], options: KeyframeAnimationOptions) => {
    return new Promise<void>(resolve => {
      const animation = element.animate(keyframes, options)
      animation.onfinish = () => resolve()
    })
  }

  const createConfetti = () => {
    for (let i = 0; i < 30; i++) {
      const particle = document.createElement('div')

      particle.className = 'star-particle'
      particle.style.filter = `hue-rotate(${Math.floor(Math.random() * 360)}deg)`

      confetti.appendChild(particle)

      particle.animate(
        [
          { transform: 'translateY(0)', opacity: 1 },
          { transform: `translate(${Math.random() * 100 - 50}px, ${Math.random() * 100 - 50}px)`, opacity: 0 }
        ],
        {
          duration: 500,
          easing: 'ease-out'
        }
      )
    }
  }

  const animate = async () => {
    await animatePromise(activeStar, [{ transform: 'scale(1)' }, { transform: 'scale(1.5)' }], {
      duration: 200,
      easing: 'ease-out'
    })

    createConfetti()
    setTimeout(() => confetti.remove(), 500)

    activeStar.hidden = true
    inactiveStar.hidden = false

    await animatePromise(inactiveStar, [{ transform: 'scale(1.5)' }, { transform: 'scale(1)' }], {
      duration: 200,
      easing: 'ease-out'
    })
  }

  void animate()
}

const getStars = async () => {
  try {
    const resp = await httpPost<{ failed?: boolean; too_many_stars?: boolean; award_count?: number }>(
      '/student/smart_book/request_award_night_listening',
      {
        student_id: props.context.student.id,
        course_id: props.context.course_part.course_id
      }
    )

    if (resp.failed) throw new Error('Night star request failed')
    if (resp.too_many_stars || !resp.award_count) return 0

    toggleStar()
    emit('add-stars', resp.award_count)
    return resp.award_count
  } catch (err) {
    console.error(err)
    return 0
  }
}

const $open = async (id: number) => {
  isOpen.value = true
  materialId.value = id

  await nextTick()
  for (let i = 0; i < starCount.value; i++) toggleStar()

  const audioElement = audio.value?.$el.querySelector('audio') as HTMLAudioElement | null
  if (!audioElement) return

  tracker = new AudioActivityTracker(audioElement, async (ms: number) => {
    const percent = audioElement.currentTime / audioElement.duration

    if (percent > 0.95 && !starCount.value) {
      starCount.value++
      await getStars()
    }

    try {
      const resp = await httpPost<{ seconds_to_discharge?: number; subcharge_percent?: number }>(
        '/student/track_material_activity',
        {
          student_id: props.context.student.id,
          material_id: id,
          course_id: props.context.course_part.course_id,
          milliseconds: ms,
          night_mode: true
        }
      )

      if (resp.seconds_to_discharge) emit('update:discharge', resp.seconds_to_discharge)
      if (resp.subcharge_percent) emit('update:subcharge', resp.subcharge_percent)
    } catch {
      // noop
    }
  })

  if (props.canSubcharge) {
    subchargeTracker = new AudioActivityTracker(
      audioElement,
      (ms: number) => {
        const percentToAdd = ms / (audioElement.duration * 1000)
        emit('add-subcharge', percentToAdd * 100)
      },
      5000
    )

    subchargeTracker.onAudioPlayed()
  }

  tracker.onAudioPlayed()

  closeInterval = setTimeout(() => {
    if (new Date().getHours() >= 20) return
    if (sessionStorage.overrideNightMode) {
      console.warn("Night player would've closed now, but override mode is active")
      return
    }
    close()
  }, 30000)
}

const onAudioEnd = () => {
  if (repeatCount.value < 1) return close()

  repeatCount.value--
  const audioElement = audio.value?.$el.querySelector('audio') as HTMLAudioElement | null
  if (!audioElement) return
  audioElement.currentTime = 0
  void audioElement.play()
}

defineExpose({ $open })

onBeforeUnmount(() => {
  if (closeInterval) clearTimeout(closeInterval)
  tracker?.onAudioPaused()
  subchargeTracker?.onAudioPaused()
})
</script>

<template>
  <v-rich-modal
    :model-value="isOpen"
    persistent
    class="student-night-player-modal"
    :class="{ 'student-night-player-modal_dim': dim }"
    force-desktop
  >
    <div class="d-flex align-center mb-2">
      <span class="text-h3 flex-spacer mr-2">Good night!</span>

      <v-icon
        class="gray-text mr-1"
        @click="
          () => {
            oneStarInfo = true
          }
        "
      >
        mdi-help-circle-outline
      </v-icon>

      <div ref="star" class="student-night-player-modal__star orange-text">
        <v-icon size="32" hidden>mdi-star</v-icon>
        <v-icon size="32">mdi-star-outline</v-icon>
      </div>

      <v-link
        class="ml-2"
        @click="
          () => {
            closing = true
          }
        "
      >
        <v-icon size="28">mdi-close</v-icon>
      </v-link>
    </div>

    <v-rich-modal v-model="closing" title="Вы уверены?" class="student-night-player-modal__submodal">
      <p style="max-width: 450px">
        Вы собираетесь отключить ночной режим. В ночном режиме информация лучше запоминается, и за полное прослушивание
        начисляется дополнительная звёздочка. Вы уверены, что хотите закрыть режим?
      </p>

      <template #actions>
        <v-button
          mode="outlined block"
          @click="
            () => {
              closing = false
            }
          "
        >
          Остаться
        </v-button>

        <v-button mode="primary filled block" @click="close"> Да, выйти </v-button>
      </template>
    </v-rich-modal>

    <v-rich-modal v-model="oneStarInfo" title="Информация" class="student-night-player-modal__submodal">
      <p style="max-width: 450px">
        Вы отлично используете возможности ночного режима! За полное ночное прослушивание в этом курсе выдаётся
        {{ awardCountWithNoun }}.
      </p>

      <template #actions>
        <v-button
          mode="primary filled block"
          @click="
            () => {
              oneStarInfo = false
            }
          "
        >
          Понятно
        </v-button>
      </template>
    </v-rich-modal>

    <AudioPlayerV2
      v-if="material"
      ref="audio"
      :student-id="context.student.id"
      :material="material"
      @ended="onAudioEnd"
    >
      <template #controls>
        <v-menu-base pos-horizontal="right">
          <template #activator="{ onclick }">
            <div class="student-night-player-modal__repeat-btn" @click="onclick">
              <v-icon size="32" class="orange-text">mdi-repeat</v-icon>
              {{ repeatCount || ' ' }}
            </div>
          </template>

          <div class="pa-1" style="max-height: 40vh; overflow-y: auto">
            <p class="pa-1 gray-text">Повторить</p>
            <v-menu-divider />

            <v-menu-button
              v-for="i in 4"
              :key="i"
              @click="
                () => {
                  repeatCount = i - 1
                }
              "
            >
              <template v-if="i === 1">Выкл.</template>
              <template v-else>{{ formatPlural(i - 1, 'раз', 'раза', 'раз') }}</template>

              <template v-if="repeatCount === i - 1" #append>
                <v-icon class="green-text">mdi-check</v-icon>
              </template>
            </v-menu-button>
          </div>
        </v-menu-base>
      </template>
    </AudioPlayerV2>
  </v-rich-modal>
</template>

<style lang="scss">
.student-night-player-modal {
  & > .v-rich-modal__container {
    overflow: visible;
  }

  & > .v-rich-modal__container > .v-rich-modal__content-wrapper > .v-rich-modal__content {
    width: min(600px, 90vw);
    @media (orientation: portrait) {
      width: min(600px, 90vh);
    }

    background: #ffe4b9;
    padding: 16px 16px;
    border-radius: 24px;
    overflow: visible;

    transition: filter 0.5s ease;
  }

  & > .v-rich-modal__background {
    background: rgba(25, 25, 25, 0.8);
  }

  &_dim > .v-rich-modal__container > .v-rich-modal__content-wrapper > .v-rich-modal__content {
    filter: brightness(0.7);
  }

  .student-v2-audio-player {
    background: white;
  }

  .student-v2-audio-player__controls-row:last-child {
    display: none;
  }

  &__star {
    position: relative;

    & > [hidden] {
      display: none !important;
    }
  }

  &__repeat-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;

    width: 32px;
    height: 32px;

    @include text-caption;
    font-weight: bold;

    & > .v-icon {
      position: absolute;
    }
  }

  .star-particle {
    position: absolute;
    top: 12px;
    left: 12px;

    width: 4px;
    height: 4px;
    background: #fc3737;
    border-radius: 50%;
  }
}
</style>
